/**
 * Read-only classification facts shared by the review row and the opened prompt:
 * `LevelLine` (UI-SPEC §2.7, referral; a generic → specific bar since 2026-11-04) and `MetaLine` (criteria, UI-SPEC §2.8; the
 * theme / use case line is an owner addition, 2026-10-20).
 */
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { BrandQueryAxis, ComplementKind, TargetZone } from '@traaker/shared';
import { levelFromComplements } from '@traaker/shared';

/** The 5 steps of the referral level, generic → specific (the hover title of the bar). */
const LEVEL_STEP_KEY: Record<string, string> = {
  L5: 'promptStudio.level.step.generic',
  L4: 'promptStudio.level.step.category',
  L3: 'promptStudio.level.step.middle',
  L2: 'promptStudio.level.step.precise',
  L1: 'promptStudio.level.step.veryPrecise',
};

const line = { fontSize: 11.5, color: 'text.secondary', cursor: 'default', mb: 0.5 } as const;

/**
 * Recommandation: how specific the prompt is, as a bar from « Générique » to « Spécifique » with
 * its 5 steps (owner 2026-11-04: no level word, no « N compléments » reason any more). The level
 * is the C5 one: computed from the complements + zone when known, else the stored `level`.
 */
export function LevelLine({
  level,
  complements,
  zone,
}: {
  level?: string | null;
  complements?: ComplementKind[];
  zone?: TargetZone | null;
}) {
  const { t } = useTranslation();
  const computed = complements ? levelFromComplements(complements, zone ?? null) : null;
  const lvl = computed?.level ?? level;
  if (!lvl || !LEVEL_STEP_KEY[lvl]) return null;
  const lit = 6 - Number(lvl.slice(1));
  return (
    <Box sx={{ ...line, display: 'flex', alignItems: 'center', gap: 0.75 }} title={t(LEVEL_STEP_KEY[lvl])}>
      <Box component="span">{t('promptStudio.level.generic')}</Box>
      <Box sx={{ display: 'flex', gap: '3px' }} role="img" aria-label={t(LEVEL_STEP_KEY[lvl])}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Box key={i} data-lit={i < lit ? 'on' : 'off'} sx={{ width: 16, height: 5, borderRadius: '3px', bgcolor: i < lit ? '#be185d' : '#e4e4ea' }} />
        ))}
      </Box>
      <Box component="span">{t('promptStudio.level.specific')}</Box>
    </Box>
  );
}

const TARGET_WORD: Record<string, string> = {
  // i18n: promptStudio.target.category.*
  marque: 'marque', etablissement: 'établissement', personne: 'personne', produit: 'produit',
};

export function MetaLine({
  axis,
  criteria,
  topic,
  target,
  title,
  targetOnly,
}: {
  axis?: BrandQueryAxis | 'pending';
  criteria?: string[];
  topic?: string;
  /** Image / Comparaison: the target alone (the criteria are edited as chips beside it). */
  targetOnly?: boolean;
  /** Image / Comparaison only (UI-SPEC §2.8): `undefined` = no target fact, `null` = « cible non détectée ». */
  target?: { label: string; category: string } | null;
  title?: string;
}) {
  if ((axis === 'narrative' || axis === 'comparison') && targetOnly) {
    if (target === undefined) return null;
    return (
      <Box sx={line} title={title}>
        {target
          ? `${target.label}, ${TARGET_WORD[target.category] ?? target.category}`
          : 'cible non détectée' /* i18n: promptStudio.meta.noTarget */}
      </Box>
    );
  }
  if (axis === 'narrative' || axis === 'comparison') {
    return (
      <Box sx={line} title={title}>
        {criteria?.length ? criteria.join(', ') : 'aucun critère' /* i18n: promptStudio.meta.noCriteria */}
        {target !== undefined && (
          <>
            {' · '}
            {target
              ? `${target.label}, ${TARGET_WORD[target.category] ?? target.category}`
              : 'cible non détectée' /* i18n: promptStudio.meta.noTarget */}
          </>
        )}
      </Box>
    );
  }
  if ((axis === 'authority' || axis === 'referral') && topic) {
    return (
      <Box sx={line} title={title}>
        {axis === 'authority' ? 'Thème' : 'Cas d’usage' /* i18n: promptStudio.meta.topic.* */} : {topic}
      </Box>
    );
  }
  return null;
}
